import DataGrid, { Column, DataGridTypes, Export, Grouping, GroupPanel, Pager, Paging } from "devextreme-react/data-grid";
import { useCallback, useMemo, useState } from "react";
import axios from 'axios'
import Swal from 'sweetalert2'
import apiUrl from '../../configs/constant';
import { useNavigate } from "react-router-dom";
import { Box, Button, Chip, IconButton, InputAdornment, Skeleton, Stack, TextField, Typography } from "@mui/material";
import AddCircleIcon from '@mui/icons-material/AddCircle'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { CheckBox } from "devextreme-react";
import { Workbook } from "exceljs";
import { saveAs } from 'file-saver-es';
import { exportDataGrid } from "devextreme/excel_exporter";
import Icon from '../icon';
import './msiDatagrid.css';

const normalizeSearch = (value: unknown) => String(value ?? '')
  .toLocaleLowerCase('id-ID')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim();

const getColumnWidth = (column: string) => {
  if (/(^|_)(qty|jumlah|saldo|stok|stock|masuk|keluar|harga|total|nilai|berat)(_|$)/i.test(column)) return 140;
  if (/(nama|keterangan|deskripsi|description|alamat|jenis)/i.test(column)) return 240;
  if (/(tanggal|tgl|status|satuan|kode|sku|id)$/i.test(column)) return 150;

  return 180;
};

export interface ActionColumn {
  key: string;
  flex?: number;
  sortable?: boolean;
  minWidth?: number;
  field?: string;
  align?: "left" | "right" | "center" | string;
  headerAlign?: "left" | "right" | "center" | string;
  headerName?: string;
  caption?: string;
  renderCell: (params: { row: any; data?: any }) => React.ReactNode;
}
interface CustomRender {
  key: string;
  renderCell: (cellData: any) => React.ReactNode;
}
interface Props {
  array: any[];
  handleSelection?: any;
  rowClick?: any;
  cellClick?: any;
  toolbarPreparing?: any;
  formRender?: any[];
  groupBy: any[];
  gudangId?:any;
  customRender?: CustomRender[];
  hiddenColumns?: string[];
  actions?: ActionColumn[];
  actionsCaption?: string;
  groupPanel?: boolean;
  loading?: boolean;
  title?: string;
  searchPlaceholder?: string;

  onRowPrepared?: any;
  rowStyleRules?: {
    field: string;
    operator?: "==" | "!=" | ">" | "<" | ">=" | "<=";
    value: any;
    color: string;
  }[]
};

export default function MsiDatagridGroup(props: Props) {
  const userData = JSON.parse(localStorage.getItem('user')!)
  const token = userData.token
  const [autoExpandAll, setAutoExpandAll] = useState(false);
  const onAutoExpandAllChanged = useCallback(() => {
    setAutoExpandAll((previousAutoExpandAll) => !previousAutoExpandAll);
  }, []);
  const navigate = useNavigate();

  const {
    array,
    customRender = [],
    hiddenColumns = [],
    actions = [],
    groupBy = [],
    gudangId = null,
    actionsCaption,
    rowStyleRules = [],
    loading = false,
    title = 'Data terkelompok',
    searchPlaceholder = 'Cari di semua kolom...',
    onRowPrepared: propsOnRowPrepared,
  } = props;
  const [searchText, setSearchText] = useState('');

  const filteredRows = useMemo(() => {
    const tokens = normalizeSearch(searchText).split(/\s+/).filter(Boolean);
    if (!tokens.length) return array;

    return array.filter(row => {
      const searchable = normalizeSearch(Object.values(row ?? {}).join(' '));
      return tokens.every(token => searchable.includes(token));
    });
  }, [array, searchText]);

  // ambil key dari data pertama
  const columns = array.length
    ? Object.keys(array[0]).filter(key => key !== 'ID')
    : [];

  const toCaption = (text: string) => {
    if (!text) return '';

    return text
      // ganti underscore jadi spasi
      .replace(/_/g, ' ')
      // kasih spasi sebelum huruf besar (camelCase / PascalCase)
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // lowercase semua dulu biar rapi
      .toLowerCase()
      // kapital huruf awal tiap kata
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  const onExporting = (e: DataGridTypes.ExportingEvent) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Main sheet');

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'DataGrid.xlsx');
      });
    });
  };

  const onRowPrepared = (e: DataGridTypes.RowPreparedEvent) => {
    if (propsOnRowPrepared) {
      propsOnRowPrepared(e);
      // continue to apply custom rules as well
    }

    if (e.rowType !== "data" || !rowStyleRules?.length) return;

    for (const rule of rowStyleRules) {
      const rowValue = e.data?.[rule.field];
      const compare = (a: any, b: any, op: string) => {
        switch (op) {
          case "==":
            return a == b;
          case "!=":
            return a != b;
          case ">":
            return a > b;
          case "<":
            return a < b;
          case ">=":
            return a >= b;
          case "<=":
            return a <= b;
          default:
            return false;
        }
      };
      if (compare(rowValue, rule.value, rule.operator ?? "==")) {
        if (e.rowElement) {
          e.rowElement.style.backgroundColor = rule.color;
          e.rowElement.style.color = "black";
        }
      }
    }
  };

  const handleTambahUsage = (id: number) => {
    Swal.fire({
      title: 'Konfirmasi',
      text: 'Apakah anda ingin menambahkan usage dari data ini?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4caf50',
      cancelButtonColor: '#ffc107',
      confirmButtonText: 'Ya, Tambahkan'
    }).then(result => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Now loading',
          allowEscapeKey: false,
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading()
        })

        const url = `${apiUrl.apiUrl}/mm/usage/add-from-wo/${id}`

        axios
          .post(url, {mmgudangId : gudangId}, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then(response => {
            if (response.data.success) {
              Swal.fire({
                title: 'Berhasil',
                text: response.data.message,
                icon: 'success',
                timer: 1200,
                timerProgressBar: true,
                willClose: () => navigate('/mm/usage/detail/' + response.data.data)
              })
            } else {
              Swal.fire({
                title: 'Gagal',
                text: response.data.message,
                icon: 'error'
              })
            }
          })
          .catch(error => {
            Swal.fire({
              title: 'Error',
              text: error.message,
              icon: 'error'
            })
          })
      }
    })
  }

  const handleTambahPR = (id: number) => {
    Swal.fire({
      title: 'Konfirmasi',
      text: 'Apakah anda ingin membuat Purchase Request dari data ini?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#9c27b0',
      cancelButtonColor: '#ffc107',
      confirmButtonText: 'Buat PR'
    }).then(result => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Now loading',
          allowEscapeKey: false,
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading()
        })

        const url = `${apiUrl.apiUrl}/purchasing/pr/add-from-wo/${id}`

        axios
          .post(url, {}, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then(response => {
            if (response.data.success) {
              Swal.fire({
                title: 'Berhasil',
                text: response.data.message,
                icon: 'success',
                timer: 1200,
                timerProgressBar: true,
                willClose: () => navigate('/purchasing/purchase-request/detail/' + response.data.data)
              })
            } else {
              Swal.fire({
                title: 'Gagal',
                text: response.data.message,
                icon: 'error'
              })
            }
          })
          .catch(error => {
            Swal.fire({
              title: 'Error',
              text: error.message,
              icon: 'error'
            })
          })
      }
    })
  }



  return (
    <Box className="msi-grid-shell">
      <Box className="msi-grid-toolbar">
        <Box>
          <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {loading ? 'Memuat data...' : `${filteredRows.length} dari ${array.length} baris`}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            value={searchText}
            onChange={event => setSearchText(event.target.value)}
            placeholder={searchPlaceholder}
            size="small"
            className="msi-grid-search"
            InputProps={{
              startAdornment: <InputAdornment position="start"><Icon icon="mdi:magnify" width={20} /></InputAdornment>,
              endAdornment: searchText
                ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchText('')} aria-label="Hapus pencarian"><Icon icon="mdi:close" width={18} /></IconButton></InputAdornment>
                : undefined
            }}
          />
          <CheckBox
            text="Expand Semua Data"
            id="autoExpand"
            value={autoExpandAll}
            onValueChanged={onAutoExpandAllChanged}
          />
        </Stack>
      </Box>

      {loading ? (
        <Box className="msi-grid-loading">
          <Skeleton variant="rounded" height={44} />
          {[0, 1, 2, 3, 4].map(row => <Skeleton key={row} height={42} />)}
        </Box>
      ) : (
      <DataGrid
        dataSource={filteredRows}
        keyExpr="id"
        noDataText={searchText ? 'Data yang dicari tidak ditemukan' : 'Belum ada data untuk ditampilkan'}
        showBorders={false}
        showColumnLines={false}
        showRowLines={true}
        rowAlternationEnabled={true}
        hoverStateEnabled={true}
        wordWrapEnabled={false}
        allowColumnResizing={true}
        allowColumnReordering={true}
        columnAutoWidth={false}
        width="100%"
        onExporting={onExporting}
        onRowPrepared={onRowPrepared}
      >
        <Column
          dataField="preview"
          caption="Preview"
          groupIndex={0}
          groupCellRender={(e: any) => {
            const preview = e.value

            return (
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ width: '100%' }}
              >
                {/* Label Group */}
                <Chip
                  label={`Preview: ${preview}`}
                  size="small"
                />

                {/* Tambah Usage */}
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<AddCircleIcon />}
                  onClick={(ev) => {
                    ev.stopPropagation()

                    const data = e.data || {}

                    const id =
                      data?.collapsedItems?.[0]?.id ??
                      data?.items?.[0]?.id

                    console.log("ID:", id)

                    handleTambahUsage(id)
                  }}
                >
                  Usage
                </Button>

                {/* Tambah PR */}
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<ShoppingCartIcon />}
                  onClick={(ev) => {
                    ev.stopPropagation()
                    const id = e.data?.collapsedItems?.[0]?.id
                    handleTambahPR(id)
                  }}
                >
                  PR
                </Button>
              </Stack>
            )
          }}
        />
        {actions
          .map(act => (
            <Column
              key={act.key}
              dataField={act.field ?? act.key}
              caption={act.headerName ?? act.caption ?? actionsCaption ?? "ACTIONS"}
              alignment={(act.align as any) || "left"}
              minWidth={act.minWidth}
              allowSorting={act.sortable ?? false}
              cellRender={({ data }: any) => act.renderCell({ row: data, data })}
              allowEditing={false}
            // don't set width so columnAutoWidth can decide
            />
          ))}
        {columns
          .filter(col => col !== 'preview')
          .filter(col => !hiddenColumns.includes(col))
          .map(col => (
            <Column
              key={col}
              dataField={col}
              caption={toCaption(col)}
              width={getColumnWidth(col)}
              cellRender={({ value }: any) => (
                <span className="msi-cell-one-line" title={String(value ?? '')}>{value}</span>
              )}
            />
          ))}
        <GroupPanel visible={true} />
        <Grouping autoExpandAll={autoExpandAll} />
        <Paging defaultPageSize={20} />
        <Pager visible={true} />
        <Export enabled={true} />
      </DataGrid>
      )}
    </Box>
  );
}
